import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';

interface AreaChartProps {
  tasks: { name: string; data: number[] }[];
  categories: string[];
  onTimeRangeChange?: (range: '5D' | '2W' | '1M' | '6M' | '1Y') => void;
  currentTimeRange?: string;
}

const AreaChart: React.FC<AreaChartProps> = ({ 
  tasks, 
  categories, 
  onTimeRangeChange,
  currentTimeRange = '5D'
}) => {
  const [state, setState] = useState({
    series: [],
    options: {
      chart: {
        id: 'area-chart',
        type: 'area' as const,
        height: 350,
        zoom: { autoScaleYaxis: true },
        background: 'transparent',
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      markers: { size: 0, style: 'hollow' as const },
      xaxis: {
        categories: categories,
        type: 'category' as const,
        labels: {
          show: true,
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          style: {
            fontSize: '12px',
            colors: '#525866',
          },
        },
        axisBorder: {
          show: true,
          color: '#E2E4E9',
        },
      },
      tooltip: { 
        enabled: true,
        x: {
          show: true,
        },
        y: {
          formatter: function (val: number) {
            return val + " users";
          }
        }
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        tickAmount: 4,
        labels: {
          show: true,
          style: {
            colors: ['#525866'],
            fontSize: '12px',
          },
          formatter: function (val: number) {
            return `${Math.round(val)}`;
          }
        },
      },
      grid: {
        strokeDashArray: 4,
      },
      fill: {
        type: 'gradient' as const,
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 100],
        },
      },
      plotOptions: {
        area: {
          fillTo: 'origin' as const,
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['#4F46E5'],
      },
    },
  });

  // ✅ Sync props to chart state
  useEffect(() => {
    if (tasks.length && categories.length) {
      setState((prev: any) => ({
        ...prev,
        series: [{ name: tasks[0].name, data: tasks[0].data }],
        options: {
          ...prev.options,
          xaxis: { 
            ...prev.options.xaxis, 
            categories,
            labels: {
              ...prev.options.xaxis.labels,
              // Adjust rotation based on time range
              rotate: currentTimeRange === '1Y' || currentTimeRange === '6M' || currentTimeRange === '1M' ? -45 : 0,
            }
          },
        },
      }));
    }
  }, [tasks, categories, currentTimeRange]);

  const handleTimeRangeClick = (range: '5D' | '2W' | '1M' | '6M' | '1Y') => {
    if (onTimeRangeChange) {
      onTimeRangeChange(range);
    }
  };

  // ✅ Show fallback if no data
  if (!tasks.length || !categories.length) {
    return <div>No data available</div>;
  }

  return (
    <div style={{ background: 'transparent' }}>
      <div className="toolbar area-chart-toolbar" style={{ marginBottom: '1rem' }}>
        {['5D', '2W', '1M', '6M', '1Y'].map((range) => (
          <button 
            key={range} 
            onClick={() => handleTimeRangeClick(range as '5D' | '2W' | '1M' | '6M' | '1Y')}
            className={currentTimeRange === range ? 'active' : ''}
          >
            {range}
          </button>
        ))}
      </div>

      <ReactApexChart
        options={state.options}
        series={state.series}
        type="area"
        height={350}
      />
    </div>
  );
};

export default AreaChart;
